import type { MaterialDto } from '@ads/shared';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { createPortal } from 'react-dom';
import * as Ic from '../components/icons';
import { uploadText, useEvidence, useMaterialAction, useUpload } from '../lib/queries';
import { MaterialCard } from './InicioPanels';

/* ─────────────────────────────────────────────────────────────────────
   Evidências de um item de uma ferramenta do Strategy — um indicador do
   Mapa de Gaps, um diferencial competitivo. Envia, consulta, visualiza
   (o texto que o ADS leu, ou o próprio arquivo quando é PDF ou imagem)
   e exclui. Cada arquivo é lido pelo agente e entra na leitura dele.
   ───────────────────────────────────────────────────────────────────── */

const VIEWABLE = /^(application\/pdf|image\/(png|jpeg|webp|gif))$/;

export interface EvidenceTarget {
  /** "<ferramenta>:<id do item>" */
  ref: string;
  title: string;
  kicker: string;
  lede: string;
  /** o que dizer quando ainda não há documento */
  empty: string;
}

export function EvidenceModal({ target, onClose }: { target: EvidenceTarget; onClose: () => void }) {
  const { ref } = target;
  const all = useEvidence();
  const upload = useUpload();
  const act = useMaterialAction();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<string | null>(null);
  const items = (all.data ?? []).filter(e => e.ref === ref);
  const viewing = items.find(i => i.id === view) ?? null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    addEventListener('keydown', onKey);
    const prev = document.activeElement as HTMLElement | null;
    return () => { removeEventListener('keydown', onKey); prev?.focus?.(); };
  }, [onClose]);

  const send = async (files: FileList | File[]) => {
    setErr(null);
    for (const f of Array.from(files)) {
      setBusy(b => [...b, f.name]);
      try { await upload.mutateAsync({ file: f, kind: 'document', ref }); }
      catch (e) { setErr(`${f.name}: ${e instanceof Error ? e.message : 'falha no envio.'}`); }
      finally { setBusy(b => b.filter(x => x !== f.name)); }
    }
  };

  return createPortal(
    <div className="pub-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="pub ev" role="dialog" aria-modal="true" aria-labelledby="evTitle">
        <header className="pub-h">
          <div>
            <span className="pub-kicker"><Ic.Clip />{target.kicker}</span>
            <h2 id="evTitle">{target.title}</h2>
          </div>
          <button className="pub-x" type="button" onClick={onClose} aria-label="Fechar" autoFocus><Ic.Close /></button>
        </header>
        <div className="pub-body">
          <p className="pub-lede">{target.lede}</p>

          <div className="drop ev-drop" tabIndex={0} role="button" aria-label="Enviar evidências"
            onClick={() => input.current?.click()}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current?.click(); } }}
            onDragOver={(e: DragEvent) => e.preventDefault()}
            onDrop={(e: DragEvent) => { e.preventDefault(); if (e.dataTransfer.files.length) void send(e.dataTransfer.files); }}>
            <span className="ico"><Ic.Upload /></span>
            <div className="t">Arraste os documentos aqui</div><div className="s">ou clique para selecionar</div>
            <div className="fmt mono">PDF · Word · PowerPoint · Excel · imagem — até 50 MB</div>
          </div>
          <input ref={input} type="file" hidden multiple accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.csv,.zip,.png,.jpg,.jpeg,.webp"
            onChange={e => { if (e.target.files) void send(e.target.files); e.target.value = ''; }} />

          {err && <p className="cfg-status bad ev-err"><Ic.Warning />{err}</p>}

          <div className="mat-h"><h4>{items.length ? `${items.length} ${items.length === 1 ? 'documento' : 'documentos'}` : 'Nenhum documento ainda'}</h4>
            {!items.length && <span>{target.empty}</span>}</div>
          <ul className="mats ev-list">
            {items.map(m => (
              <MaterialCard key={m.id} m={m} viewing={view === m.id}
                onView={() => setView(v => (v === m.id ? null : m.id))}
                onAction={a => { if (a === 'delete' && view === m.id) setView(null); act.mutate({ id: m.id, action: a }); }} />
            ))}
            {busy.map(n => (
              <li key={'b' + n} className="mitem pending">
                <span className="mat-ic"><b>{(/\.([a-z0-9]{1,5})$/i.exec(n)?.[1] ?? '—').toUpperCase()}</b></span>
                <div className="mat-main"><div className="mat-top"><span className="mat-name">{n}</span></div>
                  <div className="mat-meta"><span className="mat-st busy"><span className="spin" />Enviando…</span></div></div>
              </li>
            ))}
          </ul>

          {viewing && <Preview m={viewing} onClose={() => setView(null)} />}
        </div>
        <footer className="pub-f">
          <span className="ev-foot">{items.filter(i => i.reading === 'read').length} de {items.length} lidos pelo ADS</span>
          <button className="btn-primary" type="button" onClick={onClose}>Concluir<Ic.Check /></button>
        </footer>
      </aside>
    </div>,
    document.body,
  );
}

/* ── visualizar: a imagem, ou o texto que o ADS leu, seção por seção ── */

function Preview({ m, onClose }: { m: MaterialDto; onClose: () => void }) {
  const [text, setText] = useState<Awaited<ReturnType<typeof uploadText>> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const isImage = m.contentType.startsWith('image/') && m.contentType !== 'image/svg+xml';
  const openUrl = `/api/uploads/${m.id}/content${VIEWABLE.test(m.contentType) ? '?view=1' : ''}`;

  useEffect(() => {
    let alive = true;
    setText(null); setErr(null);
    if (!isImage) uploadText(m.id).then(t => { if (alive) setText(t); }, e => { if (alive) setErr(e instanceof Error ? e.message : 'Não foi possível carregar.'); });
    return () => { alive = false; };
  }, [m.id, m.readAt, isImage]);

  return (
    <section className="ev-view" aria-label={`Visualização de ${m.filename}`}>
      <div className="ev-view-h">
        <b>{m.catalog?.title ?? m.filename}</b>
        <span>
          <a className="ev-open" href={openUrl} target="_blank" rel="noopener">{VIEWABLE.test(m.contentType) ? 'Abrir o arquivo' : 'Baixar o arquivo'}<Ic.External /></a>
          <button type="button" className="pub-x sm" onClick={onClose} aria-label="Fechar visualização"><Ic.Close /></button>
        </span>
      </div>
      {isImage ? (
        <img className="ev-img" src={`/api/uploads/${m.id}/content?view=1`} alt={m.catalog?.summary ?? m.filename} />
      ) : err ? (
        <p className="cfg-status bad"><Ic.Warning />{err}</p>
      ) : !text ? (
        <div className="obj-skel"><i /><i /><i /></div>
      ) : text.sections.length ? (
        <div className="ev-text">
          {text.sections.map((s, i) => (
            <div key={i} className="ev-sec">{s.label && <em>{s.label}</em>}<p>{s.text}</p></div>
          ))}
          {text.truncated && <p className="muted-sm">O documento continua — abra o arquivo para ver o resto.</p>}
        </div>
      ) : (
        <p className="muted-sm">{m.reading === 'read' || m.reading === 'skipped' ? 'O ADS não encontrou texto neste arquivo. Abra o arquivo para ver.' : 'O ADS ainda está lendo este arquivo.'}</p>
      )}
    </section>
  );
}
